import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Slider,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaletteIcon from "@mui/icons-material/Palette";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  ColorAdjustment,
  ThemeAdjustments,
  createAppTheme,
  defaultAdjustments,
  themePalettes,
} from "../theme";

const ADJ_STORAGE_KEY = "humaj-dev-theme-adj";
// Webpack DefinePlugin (used by Gatsby) inlines `process.env.NODE_ENV` at build
// time, so this expression resolves to a literal boolean and dead-code-
// eliminates the dev panel entirely from production bundles.
const IS_DEV = process.env.NODE_ENV === "development";

interface AppThemeProviderProps {
  children: React.ReactNode;
}

const AppThemeProvider: React.FC<AppThemeProviderProps> = ({ children }) => {
  const [adjustments, setAdjustments] = useState<ThemeAdjustments>(
    defaultAdjustments
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  // Read persisted slider values once on mount (avoids SSR/hydration mismatch).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(ADJ_STORAGE_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored) as ThemeAdjustments;
      if (
        parsed?.primary &&
        parsed?.secondary &&
        typeof parsed.primary.saturationDelta === "number" &&
        typeof parsed.primary.lightnessDelta === "number" &&
        typeof parsed.secondary.saturationDelta === "number" &&
        typeof parsed.secondary.lightnessDelta === "number"
      ) {
        setAdjustments(parsed);
      }
    } catch {
      /* ignore corrupted JSON */
    }
  }, []);

  // Always use the default palette — the dev panel only tunes its intensity.
  const theme = useMemo(
    () => createAppTheme("default", adjustments),
    [adjustments]
  );

  const updateAdjustment = (
    color: keyof ThemeAdjustments,
    field: keyof ColorAdjustment,
    value: number
  ) => {
    setAdjustments((prev) => {
      const next: ThemeAdjustments = {
        ...prev,
        [color]: { ...prev[color], [field]: value },
      };
      if (typeof window !== "undefined") {
        window.localStorage.setItem(ADJ_STORAGE_KEY, JSON.stringify(next));
      }
      return next;
    });
  };

  const resetAdjustments = () => {
    setAdjustments(defaultAdjustments);
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(ADJ_STORAGE_KEY);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {children}
      {IS_DEV && (
        <DevColorTuner
          adjustments={adjustments}
          onChange={updateAdjustment}
          onReset={resetAdjustments}
          open={pickerOpen}
          onToggle={() => setPickerOpen((v) => !v)}
        />
      )}
    </ThemeProvider>
  );
};

// ───────────────────────────────────────────────────────────────────────────────
// Dev-only floating color tuner — sliders for primary (granat) + secondary (złoto)
// ───────────────────────────────────────────────────────────────────────────────

interface DevColorTunerProps {
  adjustments: ThemeAdjustments;
  onChange: (
    color: keyof ThemeAdjustments,
    field: keyof ColorAdjustment,
    value: number
  ) => void;
  onReset: () => void;
  open: boolean;
  onToggle: () => void;
}

const DevColorTuner: React.FC<DevColorTunerProps> = ({
  adjustments,
  onChange,
  onReset,
  open,
  onToggle,
}) => {
  // Use the default palette swatches for visual reference in the panel header.
  const palette = themePalettes.default;

  const isDirty =
    adjustments.primary.saturationDelta !== 0 ||
    adjustments.primary.lightnessDelta !== 0 ||
    adjustments.secondary.saturationDelta !== 0 ||
    adjustments.secondary.lightnessDelta !== 0;

  return (
    <>
      <IconButton
        aria-label={open ? "Zamknij panel motywu" : "Otwórz panel motywu"}
        onClick={onToggle}
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          width: 52,
          height: 52,
          borderRadius: "50%",
          bgcolor: "secondary.main",
          color: "secondary.contrastText",
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          zIndex: 9999,
          transition: "transform 0.2s ease",
          "&:hover": {
            bgcolor: "secondary.light",
            transform: "scale(1.06)",
          },
        }}
      >
        {open ? <CloseIcon /> : <PaletteIcon />}
      </IconButton>

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 86,
            right: 20,
            width: 320,
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
            p: 2,
            zIndex: 9999,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack spacing={2}>
            {/* Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "secondary.main", display: "block" }}
                >
                  Tryb dev — strojenie kolorów
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  Wartości zapisują się w localStorage.
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={onReset}
                disabled={!isDirty}
                aria-label="Reset suwaków"
                sx={{
                  color: isDirty ? "secondary.main" : "text.disabled",
                }}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Divider />

            {/* Granat (primary) */}
            <ColorSection
              label="Granat (główny)"
              swatch={palette.primary.main}
              adjustment={adjustments.primary}
              onChange={(field, value) => onChange("primary", field, value)}
            />

            <Divider />

            {/* Złoto (secondary) */}
            <ColorSection
              label="Złoto (akcent)"
              swatch={palette.secondary.main}
              adjustment={adjustments.secondary}
              onChange={(field, value) => onChange("secondary", field, value)}
            />
          </Stack>
        </Paper>
      )}
    </>
  );
};

// ───────────────────────────────────────────────────────────────────────────────
// One color group: 2 sliders (intensity + depth) with a swatch + label
// ───────────────────────────────────────────────────────────────────────────────

interface ColorSectionProps {
  label: string;
  swatch: string;
  adjustment: ColorAdjustment;
  onChange: (field: keyof ColorAdjustment, value: number) => void;
}

const ColorSection: React.FC<ColorSectionProps> = ({
  label,
  swatch,
  adjustment,
  onChange,
}) => (
  <Box>
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          bgcolor: swatch,
          border: "1px solid rgba(255,255,255,0.2)",
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
    </Stack>

    <AdjustmentSlider
      label="Intensywność"
      unit="%"
      min={-50}
      max={50}
      step={1}
      value={adjustment.saturationDelta}
      onChange={(v) => onChange("saturationDelta", v)}
    />
    <AdjustmentSlider
      label="Głębia"
      unit="%"
      min={-20}
      max={20}
      step={1}
      // "Głębia" intuicyjnie = ciemniej. Dlatego odwracamy znak na wejściu/wyjściu:
      // przesunięcie suwaka w prawo (dodatnie) = głębiej (niższa lightness).
      value={-adjustment.lightnessDelta}
      onChange={(v) => onChange("lightnessDelta", -v)}
    />
  </Box>
);

// ───────────────────────────────────────────────────────────────────────────────
// Single labeled slider with current value
// ───────────────────────────────────────────────────────────────────────────────

interface AdjustmentSliderProps {
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
}

const AdjustmentSlider: React.FC<AdjustmentSliderProps> = ({
  label,
  unit,
  min,
  max,
  step,
  value,
  onChange,
}) => (
  <Box sx={{ mb: 1 }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: -0.5 }}
    >
      <Typography variant="caption" sx={{ color: "text.primary" }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: value === 0 ? "text.secondary" : "secondary.main",
          fontVariantNumeric: "tabular-nums",
          fontWeight: value === 0 ? 400 : 600,
        }}
      >
        {value > 0 ? "+" : ""}
        {value}
        {unit}
      </Typography>
    </Stack>
    <Slider
      size="small"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(_, v) => onChange(typeof v === "number" ? v : v[0])}
      marks={[{ value: 0 }]}
      sx={{
        color: "secondary.main",
        "& .MuiSlider-mark": {
          width: 2,
          height: 8,
          bgcolor: "text.secondary",
          opacity: 0.6,
        },
        "& .MuiSlider-thumb": {
          width: 14,
          height: 14,
        },
      }}
    />
  </Box>
);

export default AppThemeProvider;
